/** Sections 28-32 - combat magic: one resolver over the closed effect union.
 *
 * The shape of this file is dictated by two rules that pull in opposite
 * directions. `SpellEffectSpec` is a closed union, so there is exactly one
 * `switch` over it and no rule anywhere names a spell id (D9); but the classic's spells
 * are not all expressible as "a number and a modifier list", so the union has an
 * `ADVENTURE` arm carrying a rule id. That arm is resolved through `BATTLE_RULES`,
 * a table keyed by the *rule* - still data, still no per-spell branch - and a
 * spell whose rule has no entry is `NOT_IMPLEMENTED` rather than a silent no-op,
 * because a cast that charges mana and does nothing is the worst of both.
 *
 * Where the caster's magic profile comes from
 * -------------------------------------------
 * `BattleHeroState` carries spell power, mana and a spellbook but no skills, so
 * school mastery, Wisdom and Spellcraft reach the kernel the way Offense and
 * Archery already do (see `heroCombatModifiers` in `init.ts`): as Modifiers on
 * the army's stacks, read back out of state so every number the formula uses is
 * hashed, journalled and replayable. `heroMagicModifiers` below produces them;
 * until `init.ts` installs them the profile reads as "not carried", which means
 * mastery 0, no Spellcraft, and no Wisdom gate - see `magicProfile`.
 *
 * Rounding: a spell's damage is `perPower * spellPower + flat` fed to
 * `resolveStat` as the base, with mastery, Spellcraft, artifacts, elemental
 * protection, vulnerability and a chaining bolt's falloff as its Modifiers.
 * `resolveStat` rounds the value exactly once (D4), so nothing here rounds at
 * all.
 *
 * The two things mastery does that are not a number
 * -------------------------------------------------
 * Expert mastery does not only raise numbers. It turns the classic's single-target
 * buffs and debuffs into their mass versions, and that is a change of
 * *targeting*: `resolveTargets` and `targetCandidates` both go through
 * `effectiveTargeting`, so Mass Bless is the same spell def with the same
 * effects and needs no second entry in the content tree. A bolt that jumps -
 * Forked Lightning - is the mirror case: the targeting is what content varies
 * (`SpellDef.chain`) and mastery has nothing to do with it.
 */

import type { ModifierSrcId, SpellDefId } from "../ids.js";
import { F2_MEDITATING } from "./flags.js";
import { asModifierSrcId } from "../ids.js";
import type { Modifier, StatKey } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { BP_ONE, FP_ONE, clampInt, mulFp, roundHalfAway } from "../numeric.js";
import type { BattleCommand, Reason } from "../commands.js";
import { Err, Ok, type Result } from "../result.js";
import type {
  ContentRegistry, CreatureAbility, SpellDef, SpellEffectSpec, SpellSchool, SpellTargeting,
} from "../content/registry.js";
import {
  BF_HEIGHT, BF_HEXES, F_BERSERK, F_BLINDED, F_PETRIFIED, F_TWO_HEX,
  type BattleState, type Obstacle, type ObstacleKind,
  hexCol, hexDistance, hexIndex, hexNeighbours, hexRow, isAlive, isConjured,
} from "./state.js";
import { F_ACTED, F_CLONE, F_HYPNOTIZED } from "./flags.js";
import { jSetField, jSetI32 } from "./journal.js";
import {
  type Ctx, addMod, clearFlags, damageStack, healStack, removeModsBySource, rollBounded,
  rollChanceBp, setFlags, setHex,
} from "./apply.js";
import { addStack, freeHexFor } from "./summon.js";
import { canOccupy, newOccupancy } from "./move.js";
import { SRC_PETRIFY, abilitiesOf, isMindImmune } from "./abilities.js";
import { attunementActive, rallyOnDeath } from "./armyPerk.js";
import { isWarMachine } from "./machines.js";
import { STAT_DAMAGE_RANGED, STAT_DAMAGE_TAKEN, effectiveDefense } from "./damage.js";
import { activeStack, reorderForWait } from "./order.js";
import { stackHexes } from "./move.js";
import {
  MASTERY_STAT, RESIST_STAT, STAT_ANTI_MAGIC, STAT_CLONE, STAT_EXTRA_RETALIATION,
  STAT_BERSERK, STAT_FIRE_SHIELD, STAT_HYPNOSIS, STAT_LIFE_DRAIN, STAT_MAGIC_MIRROR,
  STAT_MAGIC_RESIST, STRIKE_STAT,
  STAT_REGEN, sealedSchoolStat,
  STAT_IGNORE_IMMUNITY, STAT_NO_ESCAPE, STAT_NO_RAISE, STAT_SLAYER_ATTACK, STAT_SLAYER_TIER, STAT_SPELL_LEVEL_CAP,
  STAT_SPELL_LEVEL_FLOOR, STAT_WISDOM,
  schoolDamageStat, spellImmunityStat, spellSpecialtyStat, STAT_BLIND,
} from "./stats.js";

// ------------------------------------------------------------------ stat keys
//
// All minted in `stats.ts`. They are re-exported here because this is where
// every caller looks for them, and because `abilities.ts` and `damage.ts` read
// several of the same keys - a constant declared in this file would make those
// two import it and close a cycle.

export {
  MASTERY_STAT, RESIST_STAT, STAT_ANTI_MAGIC, STAT_CLONE, STAT_EXTRA_RETALIATION,
  STAT_BERSERK, STAT_FIRE_SHIELD, STAT_HYPNOSIS, STAT_LIFE_DRAIN, STAT_MAGIC_MIRROR,
  STAT_MAGIC_RESIST,
  STAT_REGEN,
  STAT_IGNORE_IMMUNITY, STAT_NO_ESCAPE, STAT_NO_RAISE, STAT_SLAYER_ATTACK, STAT_SLAYER_TIER, STAT_SPELL_LEVEL_CAP,
  STAT_SPELL_LEVEL_FLOOR, STAT_WISDOM,
  schoolDamageStat, sealedSchoolStat, spellImmunityStat, spellSpecialtyStat,
  STAT_RUNES, STAT_RUNE_CAP,
} from "./stats.js";
import { startCount } from "./revenge.js";

/** Every modifier a hero's magic profile installs shares this prefix, so a
 *  Dispel (which strips `spell.*`) can never strip the caster's own skills. */
export const MAGIC_SRC_PREFIX = "hero.magic.";
/** Everything the ground installs (Section 31); see `battle/ground.ts`. */
export const GROUND_SRC_PREFIX = "ground.";
/** Everything a spell installs is sourced at the spell's own id, so Dispel has
 *  a namespace to remove and creature abilities and hero skills are safe. */
export const SPELL_SRC_PREFIX = "spell.";

const SRC_MASTERY = asModifierSrcId("battle.spell_mastery");
const SRC_PROTECTION = asModifierSrcId("battle.spell_protection");
const SRC_VULNERABLE = asModifierSrcId("battle.spell_vulnerability");
const SRC_CHAIN = asModifierSrcId("battle.spell_chain");

const srcOf = (def: SpellDef): ModifierSrcId => asModifierSrcId(def.id as string);

// ------------------------------------------------------------------ constants

/**
 * The classic gives every damage spell its own flat-by-mastery table; `SpellEffectSpec`
 * carries a single `flat`, so advanced adds it once more and expert twice.
 * That reproduces Arcane Dart's canonical 10 / 10 / 20 / 30 exactly and is the
 * right shape for the rest. Indexed by mastery 0..3.
 */
export const MASTERY_FLAT_STEP: readonly number[] = Object.freeze([0, 0, 1, 2]);
/** Extra rounds a buff or debuff runs for at advanced and expert mastery. */
export const MASTERY_DURATION_BONUS: readonly number[] = Object.freeze([0, 0, 1, 2]);
/**
 * Wisdom caps the spell level a hero may cast: none 1-2, then one more rung per
 * skill level. This has to be the same table as `adventure/hero.ts`, which is
 * the authority - a hero who may *learn* a level-3 spell and then may not cast
 * it is not a rule, it is a disagreement between two copies of one constant.
 * Kept as a second declaration rather than an import because the battle kernel
 * does not depend on the adventure kernel; `adventure-magic.spec.ts` pins the
 * value and `battle-magic.spec.ts` pins that these two agree.
 */
export const WISDOM_SPELL_LEVEL: readonly number[] = Object.freeze([2, 3, 4, 5]);
/** Elemental protection, fixed point share removed. The expansion made these stronger. */
const PROTECTION_FP: readonly number[] = Object.freeze([300, 300, 400, 500]);
/** Null Ward seals the stack against spells up to this level. */
const ANTI_MAGIC_LEVEL: readonly number[] = Object.freeze([3, 3, 4, 5]);
/** Shield: melee damage removed. Gust Ward: ranged damage removed. */
const SHIELD_FP: readonly number[] = Object.freeze([150, 150, 150, 300]);
const AIR_SHIELD_FP: readonly number[] = Object.freeze([250, 250, 250, 500]);
/**
 * Vulnerability: damage the stack takes, ADDED rather than removed.
 *
 * Negative, because `STAT_DAMAGE_TAKEN` is a reduction and this is the same
 * stat read the other way - `damage.ts` clamps it to
 * `[-VULNERABILITY_CAP_FP, FP_ONE]`, and it used to clamp at zero, which is
 * why this spell could not exist. A quarter more at basic and advanced, a
 * third more at expert.
 *
 * It is worth less than it looks: it multiplies the blow AFTER the defence
 * bracket, so it is best on a stack that is already easy to hurt, and it does
 * nothing at all on a stack nobody can reach.
 */
export const VULNERABILITY_FP: readonly number[] = Object.freeze([250, 250, 250, 333]);
/** Befuddle takes this much off the ranged damage bracket. */
const FORGETFULNESS_FP: readonly number[] = Object.freeze([500, 500, 500, 500]);
/** Frenzy adds this share of the stack's defense to its attack, then zeroes it. */
const FRENZY_FP: readonly number[] = Object.freeze([1000, 1000, 1500, 2000]);
/** Wall sections Earthquake brings down, by mastery. */
const EARTHQUAKE_SECTIONS: readonly number[] = Object.freeze([2, 2, 3, 4]);

/** Effect kinds this kernel resolves. The rest are reported, not faked. */
const SUPPORTED_EFFECTS: readonly SpellEffectSpec["kind"][] = Object.freeze([
  "DAMAGE", "HEAL", "RESURRECT", "MODIFIER", "DISPEL", "ADVENTURE",
  "SUMMON", "CLONE", "SACRIFICE", "TELEPORT",
]);

/** Hexes a conjured barrier covers, by mastery: Flame Barrier three, Ward Wall two. */
const FIRE_WALL_HEXES: readonly number[] = Object.freeze([3, 3, 3, 3]);
const FORCE_FIELD_HEXES: readonly number[] = Object.freeze([2, 2, 2, 3]);
const QUICKSAND_PATCHES: readonly number[] = Object.freeze([1, 1, 2, 3]);
const LAND_MINE_HEXES: readonly number[] = Object.freeze([1, 1, 2, 3]);
/** Rounds a conjured barrier stands for, by mastery. */
const BARRIER_ROUNDS: readonly number[] = Object.freeze([2, 2, 2, 3]);
/** Ember Mantle: the share of a melee blow it throws back, by mastery. */
const FIRE_SHIELD_PCT: readonly number[] = Object.freeze([20, 20, 20, 30]);
/** Spell Mirror: the chance a hostile spell rebounds, in basis points. */
const MAGIC_MIRROR_BP: readonly number[] = Object.freeze([2000, 2000, 3000, 4000]);
/** Enthrall: hit points per point of spell power it can hold, by mastery. */
const HYPNOSIS_HP_PER_POWER: readonly number[] = Object.freeze([10, 10, 15, 25]);
/** Slayer: the attack it adds against the creatures it is meant for. */
const SLAYER_ATTACK = 8;
/** Slayer's quarry: tier 6 and up at basic, and everything magic cannot touch. */
const SLAYER_TIER: readonly number[] = Object.freeze([7, 6, 6, 5]);
/**
 * Silence: the four schools it seals against the other hero.
 *
 * Every school the game HAS, so the seal is total. Listed rather than derived
 * from `SpellSchool` because "all" is a member of that union and is not a
 * school a spell can be sealed out of - a spell of every school is barred by
 * the four being barred.
 */
const SEALED_SCHOOLS: readonly SpellSchool[] = Object.freeze(["air", "earth", "fire", "water"]);
/** Mana Drain: spell points taken per point of the caster's own power. */
const MANA_LEECH_PER_POWER = 2;
/**
 * Petrify: the share of incoming damage stone turns aside, in fixed point.
 *
 * The same number the creature ability uses (`abilities.ts`), so a petrified
 * stack is as hard to break however it was turned to stone - by a Stonegaze's
 * gaze or by the spell.
 */
const PETRIFY_FP = 500;
/** Riposte: extra retaliations granted, by mastery. */
const COUNTERSTRIKE_EXTRA: readonly number[] = Object.freeze([1, 1, 1, 2]);
/** Regeneration: hit points mended per turn, flat and per point of spell power. */
export const REGEN_FLAT = 10;
export const REGEN_PER_POWER = 5;
/**
 * Vampirism: the share of its own damage the target heals, in basis points.
 *
 * Capped at half deliberately. The creature ability drains in full, and a spell
 * that handed a big melee stack the Nightfeeder's whole drain would make the
 * ability worth nothing and the stack worth too much - The expansion's own Nightfeeder is
 * priced around full drain on a tier-4 body, not on a Umbral Wyrm's.
 */
export const VAMPIRISM_BP: readonly number[] = Object.freeze([3000, 3000, 3000, 5000]);
/**
 * Holy Retribution: damage per point of spell power for every creature the
 * target has killed this battle, and the ceiling it may never pass.
 *
 * The ceiling is Crush's own output at the same spell power, which is what
 * keeps a comeback lever from becoming the best level-4 spell in the book: a
 * stack that has eaten an army pays exactly as much as the heaviest single
 * blow in the game and not a point more. Everything below that ceiling is the
 * spell doing its job, which is to be worth nothing in round one.
 */
export const VENGEANCE_PER_KILL_PER_POWER = 5;
export const VENGEANCE_CAP_FLAT = 100;
export const VENGEANCE_CAP_PER_POWER = 75;

// ------------------------------------------------------------------ profile

export interface MagicProfile {
  /** False when no profile modifiers are in state; see the file header. */
  readonly carried: boolean;
  readonly spellPower: number;
  readonly mastery: number;
  readonly wisdom: number;
  /**
   * The caster's specialty in *this* spell, as a fixed-point multiplier on the
   * numbers the spell installs, and `FP_ONE` for everyone else. It is only ever
   * set for a spell that HAS numbers: the caster of a rule spell is paid in a
   * mastery rung instead (see `masteryWithSpecialty`), and never in both.
   */
  readonly specialtyFp: number;
  /** Spellcraft and artifacts, keyed `spellDamage` / `spellDuration` / `spellCost`. */
  readonly mods: readonly Modifier[];
}

function modsWithStat(mods: readonly Modifier[], stat: StatKey): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const m of mods) if (m.stat === stat) out.push(m);
  return out;
}

/**
 * The hero-wide magic modifiers carried on a side's stacks. Every stack in an
 * army gets the same list at battle start, so the first occurrence of each
 * (source, stat, op) is the whole profile; scanning all of them rather than one
 * representative stack keeps the read correct after a stack dies or is Dispelled.
 */
function sideMagicMods(s: BattleState, side: number): readonly Modifier[] {
  const seen: string[] = [];
  const out: Modifier[] = [];
  for (let i = 0; i < s.stacks.n; i++) {
    if (s.stacks.side[i] !== side) continue;
    for (const m of s.stacks.mods[i]!) {
      // The hero's own kit, and the GROUND's. A place that casts every spell
      // as an expert would is part of what a caster has to work with, so it
      // has to be in the profile the cast is resolved against - the mastery,
      // the level cap and the school damage all live there. Both prefixes are
      // safe from Dispel, which strips `spell.*`.
      const src = m.source as string;
      if (!src.startsWith(MAGIC_SRC_PREFIX) && !src.startsWith(GROUND_SRC_PREFIX)) continue;
      const key = `${src}|${m.stat}|${m.op}`;
      if (seen.includes(key)) continue;
      seen.push(key);
      out.push(m);
    }
  }
  return out;
}

/**
 * The modifiers the adventure kernel installs on every stack of a hero's army at
 * battle start, alongside `heroCombatModifiers` (which calls this).
 *
 * The Wisdom entry is always emitted, at zero if the hero has no Wisdom, because
 * `magicProfile` reads "was a profile installed at all?" off the presence of
 * these modifiers. A skill-less hero must still be gated at spell level 2, and a
 * profile that vanished when every skill happened to be zero would silently
 * hand that hero Crush.
 */
export function heroMagicModifiers(p: {
  readonly wisdom?: number;
  readonly mastery?: Partial<Record<SpellSchool, number>>;
  /** Spellcraft, fixed point: 1300 for expert. */
  readonly sorceryFp?: number;
  readonly spellCostDelta?: number;
  readonly spellDurationBonus?: number;
  /** Spell-specialty multipliers, keyed by spell id, fixed point. */
  readonly spellBonusFp?: Readonly<Record<string, number>>;
  /** School-scoped spell damage, keyed by school, fixed point. */
  readonly schoolDamageFp?: Readonly<Record<string, number>>;
}): readonly Modifier[] {
  const out: Modifier[] = [];
  const push = (name: string, stat: StatKey, op: Modifier["op"], value: number): void => {
    out.push({
      source: asModifierSrcId(`${MAGIC_SRC_PREFIX}${name}`), stat, op, value,
      expires: { kind: "battle" },
    });
  };
  push("wisdom", STAT_WISDOM, "max", clampInt(p.wisdom ?? 0, 0, 3));
  for (const school of ["air", "earth", "fire", "water"] as const) {
    const lvl = p.mastery?.[school] ?? 0;
    if (lvl > 0) push(`mastery.${school}`, MASTERY_STAT[school], "max", lvl);
  }
  if (p.sorceryFp !== undefined && p.sorceryFp !== FP_ONE) push("sorcery", "spellDamage", "mulFp", p.sorceryFp);
  if (p.spellCostDelta !== undefined && p.spellCostDelta !== 0) push("cost", "spellCost", "add", p.spellCostDelta);
  if (p.spellDurationBonus !== undefined && p.spellDurationBonus !== 0) {
    push("duration", "spellDuration", "add", p.spellDurationBonus);
  }
  // Sorted, because the order modifiers enter state is the order they hash (I3).
  for (const spell of Object.keys(p.spellBonusFp ?? {}).sort()) {
    const v = p.spellBonusFp?.[spell] ?? FP_ONE;
    if (v !== FP_ONE) push(`specialty.${spell}`, spellSpecialtyStat(spell), "mulFp", v);
  }
  for (const school of Object.keys(p.schoolDamageFp ?? {}).sort()) {
    const v = p.schoolDamageFp?.[school] ?? FP_ONE;
    if (v !== FP_ONE) push(`school.${school}`, schoolDamageStat(school), "mulFp", v);
  }
  return out;
}

/** Mastery for one spell. `all`-school spells take the best school the hero has. */
function masteryFor(mods: readonly Modifier[], school: SpellSchool): number {
  if (school === "all") {
    let best = 0;
    for (const s of ["air", "earth", "fire", "water"] as const) {
      const v = resolveStat(0, modsWithStat(mods, MASTERY_STAT[s])).value;
      if (v > best) best = v;
    }
    return clampInt(best, 0, 3);
  }
  return clampInt(resolveStat(0, modsWithStat(mods, MASTERY_STAT[school])).value, 0, 3);
}

export function magicProfile(s: BattleState, side: number, def: SpellDef): MagicProfile {
  return magicProfileFrom(s, side, def, sideMagicMods(s, side));
}

/** The same, with the side's modifier list already in hand - the enumerator
 *  gathers it once per call rather than once per candidate target. */
function magicProfileFrom(
  s: BattleState, side: number, def: SpellDef, mods: readonly Modifier[],
): MagicProfile {
  const gain = specialtyGainFp(mods, def);
  const scales = spellScalesWithSpecialty(def);
  return {
    carried: mods.length > 0,
    spellPower: s.heroes[side === 1 ? 1 : 0].spellPower,
    mastery: masteryWithSpecialty(masteryFor(mods, def.school), gain, scales),
    wisdom: clampInt(resolveStat(0, modsWithStat(mods, STAT_WISDOM)).value, 0, 3),
    specialtyFp: scales ? gain : FP_ONE,
    mods,
  };
}

/** A creature caster has no skills: its ability's `power` is the whole profile. */
function creatureProfile(power: number): MagicProfile {
  return {
    carried: false, spellPower: power, mastery: 0, wisdom: 3, specialtyFp: FP_ONE, mods: [],
  };
}

// ------------------------------------------------------- the spell specialty
//
// A spell specialty arrives here as one `mulFp` on the spell's own stat key,
// minted by `heroMagicModifiers` from `spellSpecialtyFp`. What it buys depends
// on what the spell is made of, and the two branches are exclusive:
//
//   a spell with numbers   every number the spell installs is scaled by it -
//                          damage and healing through `specialtyMods` below,
//                          and the magnitudes of a MODIFIER effect through
//                          `amplifyBySpecialty`. Lorauton's Haste is +3 speed at
//                          level 1 and +4 by level 10, rather than the flat +3
//                          every other hero casts.
//   a spell that is a rule Bless flooring damage at the top of the range has no
//                          number to scale, so the specialist casts it one
//                          mastery rung higher instead - the same trade
//                          `skillLevelBonus` makes for a rule skill, and for
//                          the same reason. He must already know the school:
//                          a rung is an amplifier, not a substitute for the
//                          skill, and a hero with no Water Magic at all casts
//                          Bless the way anyone else does.
//
// Without this, thirteen of the game's twenty-five spell specialists - every
// one whose spell neither damages nor heals - carried a specialty that did
// nothing whatever.

/**
 * Does this spell carry a number the specialty can scale? Read over the base
 * effects only: `expertBonus` is not the spell, it is what expert mastery adds
 * to it, and a rule spell whose expert rung happens to add a point of damage
 * (Bless, Curse) is still a rule spell for the hero specialised in it.
 */
export function spellScalesWithSpecialty(def: SpellDef): boolean {
  for (const e of def.effects) {
    if (e.kind === "DAMAGE" || e.kind === "HEAL" || e.kind === "RESURRECT") return true;
    if (e.kind !== "MODIFIER") continue;
    for (const m of e.modifiers ?? []) {
      if (m.op === "add" && m.value !== 0) return true;
      if (m.op === "mulFp" && m.value !== FP_ONE) return true;
    }
  }
  return false;
}

/** The specialty multiplier standing on this spell, or `FP_ONE` for none. */
function specialtyGainFp(mods: readonly Modifier[], def: SpellDef): number {
  const stat = spellSpecialtyStat(def.id as string);
  return resolveStat(FP_ONE, modsWithStat(mods, stat)).value;
}

/** The rung a rule-spell specialist casts at. Expert is still the ceiling. */
function masteryWithSpecialty(mastery: number, gainFp: number, scales: boolean): number {
  if (scales || gainFp === FP_ONE || mastery < 1 || mastery >= 3) return mastery;
  return mastery + 1;
}

/**
 * One authored modifier, scaled by the caster's specialty. An `add` is scaled
 * directly - including a debuff's negative one, which grows more negative - and
 * a `mulFp` is scaled by its distance from unity, so amplifying "-20% damage
 * taken" yields a deeper percentage rather than a second multiplication.
 * `set`, `min` and `max` are ceilings and floors rather than effects and are
 * left alone, exactly as `skillModifiersFor` leaves them.
 */
export function amplifyBySpecialty(m: Modifier, specialtyFp: number): Modifier {
  if (specialtyFp === FP_ONE) return m;
  if (m.op === "add") return { ...m, value: roundHalfAway((m.value * specialtyFp) / FP_ONE) };
  if (m.op === "mulFp") {
    return { ...m, value: FP_ONE + roundHalfAway(((m.value - FP_ONE) * specialtyFp) / FP_ONE) };
  }
  return m;
}

/**
 * Sundering Orb: is any immunity on this field suspended?
 *
 * Read from either side's field modifiers for the same reason `escapeBarred`
 * is: the orb is one object on one hero and it changes the rules for everyone
 * standing on the field, so whose kit carries it does not matter.
 */
export function immunitiesSuspended(s: BattleState): boolean {
  for (const side of [0, 1] as const) {
    const mods = sideMagicMods(s, side);
    if (resolveStat(0, modsWithStat(mods, STAT_IGNORE_IMMUNITY)).value > 0) return true;
  }
  return false;
}

/** Band of Forgetting: may anything that dies on this field be brought back? */
export function raisingBarred(s: BattleState): boolean {
  for (const side of [0, 1] as const) {
    const mods = sideMagicMods(s, side);
    if (resolveStat(0, modsWithStat(mods, STAT_NO_RAISE)).value > 0) return true;
  }
  return false;
}

/** Chains of Battle: neither hero may leave a battle either of them shackled. */
export function escapeBarred(s: BattleState): boolean {
  for (const side of [0, 1] as const) {
    const mods = sideMagicMods(s, side);
    if (resolveStat(0, modsWithStat(mods, STAT_NO_ESCAPE)).value > 0) return true;
  }
  return false;
}

export function spellManaCost(def: SpellDef, prof: MagicProfile): number {
  const base = def.cost[prof.mastery] ?? def.cost[0] ?? 0;
  const v = resolveStat(base, modsWithStat(prof.mods, "spellCost")).value;
  return v < 1 ? 1 : v;
}

/** Rounds a MODIFIER effect runs for: spell power, then mastery, then artifacts. */
export function spellDurationRounds(e: SpellEffectSpec, prof: MagicProfile): number {
  const authored = e.durationRounds ?? 0;
  const base = authored > 0 ? authored : prof.spellPower;
  const mods: Modifier[] = [...modsWithStat(prof.mods, "spellDuration")];
  const bonus = MASTERY_DURATION_BONUS[prof.mastery] ?? 0;
  if (bonus !== 0) mods.push({ source: SRC_MASTERY, stat: "spellDuration", op: "add", value: bonus });
  const v = resolveStat(base, mods).value;
  return v < 1 ? 1 : v;
}

// ------------------------------------------------------- immunity and resistance

/** Elemental protection standing on a stack, fixed point, capped at total. */
function protectionFp(s: BattleState, slot: number, school: SpellSchool): number {
  const mods = s.stacks.mods[slot]!;
  const v = resolveStat(0, modsWithStat(mods, RESIST_STAT[school])).value
    + resolveStat(0, modsWithStat(mods, RESIST_STAT.all)).value;
  return clampInt(v, 0, FP_ONE);
}

/**
 * Immunity is absolute: a Umbral Wyrm is no more blessable than burnable, and
 * an Null Ward ward seals its stack against friend and foe alike. Elemental
 * immunity is `SPELL_IMMUNITY` naming the spells of that element - it is data,
 * so no rule here knows what "fire" means.
 */
export function spellImmune(
  s: BattleState, content: ContentRegistry, slot: number, def: SpellDef,
): boolean {
  // Section 27: magic does not touch a war machine, for or against. The rule
  // matters most for the catapult, which is what a barred gate depends on: a
  // Fireball that could destroy the besieger's engine would leave a siege with
  // no way in and no way to finish.
  if (isWarMachine(s.stacks, slot)) return true;
  // Meditation: "gets hostile spell immunity" while it lasts. Above the Orb of
  // Vulnerability's line on purpose - the orb strips what a creature IS, and
  // meditation is a state the creature chose to spend a turn entering.
  if ((s.stacks.flags2[slot]! & F2_MEDITATING) !== 0) return true;
  // Everything below this line is an immunity the Sundering Orb strips.
  // The war-machine rule above it is not: it is what keeps a siege finishable.
  if (immunitiesSuspended(s)) return false;
  // The mind spells reach nothing without a mind. The classic draws the line around the
  // undead, the elementals, the sentinels and rookstones and the engines rather
  // than around a school, so it cannot be spelled as elemental immunity: the
  // spell says it is a mind spell and the creature says it has no mind.
  if (def.mind === true && isMindImmune(s.stacks.flags[slot]!)) return true;
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind === "MAGIC_IMMUNITY" && def.level <= a.maxSpellLevel) return true;
    if (a.kind === "SPELL_IMMUNITY" && a.spells.includes(def.id)) return true;
  }
  // The pendants: an artifact that makes the whole army immune to one named
  // spell, carried as a modifier on the stack rather than as a creature ability.
  const worn = resolveStat(0, modsWithStat(s.stacks.mods[slot]!, spellImmunityStat(def.id as string))).value;
  if (worn > 0) return true;
  const sealed = resolveStat(0, modsWithStat(s.stacks.mods[slot]!, STAT_ANTI_MAGIC)).value;
  return def.level <= sealed;
}

/**
 * Spell Mirror: the slot a hostile spell rebounds onto, or -1.
 *
 * Only a *hostile* cast can rebound - a hero's own Bless does not come back at
 * him - and it lands on a stack of the caster's side chosen on `battle.spell`,
 * which is the same stream resistance rolls on.
 */
function mirrored(c: Ctx, content: ContentRegistry, slot: number, casterSide: number): number {
  const st = c.s.stacks;
  if (st.side[slot] === casterSide) return -1;
  const bp = clampInt(resolveStat(0, modsWithStat(st.mods[slot]!, STAT_MAGIC_MIRROR)).value, 0, BP_ONE);
  if (bp <= 0) return -1;
  const pool: number[] = [];
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] !== casterSide || !isAlive(st, i)) continue;
    if (spellImmune(c.s, content, i, DUMMY_LEVEL_ONE)) continue;
    pool.push(i);
  }
  if (pool.length === 0) return -1;
  if (!rollChanceBp(c, "battle.spell", bp)) return -1;
  return pool[rollBounded(c, "battle.spell", pool.length)] ?? -1;
}

/**
 * A stand-in for "some spell, level one" when the mirror is choosing whom to
 * bounce onto. It only has to answer the immunity question, and every immunity
 * that would matter - a war machine, a Umbral Wyrm, an Null Ward ward - reads
 * the level or the id, both of which this carries honestly.
 */
const DUMMY_LEVEL_ONE = {
  id: "spell.__mirror" as SpellDefId, name: "", level: 1 as const, school: "all" as const,
  combat: true, adventure: false, cost: [0, 0, 0, 0] as const,
  targeting: "SINGLE_ENEMY" as const, effects: [], art: "", description: "",
} satisfies SpellDef;

/** `SPELL_VULNERABILITY`'s multiplier for this spell, or FP_ONE. */
function vulnerabilityFp(content: ContentRegistry, s: BattleState, slot: number, def: SpellDef): number {
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind === "SPELL_VULNERABILITY" && a.spells.includes(def.id)) return a.mulFp;
  }
  return FP_ONE;
}

/** Every school a strike enchantment may name. */
const SPELL_SCHOOLS: readonly SpellSchool[] =
  Object.freeze(["air", "earth", "fire", "water", "all"]);

/** The most of a charged blow that can ever slide off: 70 per cent. */
export const MAGIC_STRIKE_REDUCTION_CAP_FP = 700;

/**
 * `MAGIC_ATTACK`'s factor: the bonus the charged blow adds, times whatever the
 * target slides off. Lives here because both halves of the reduction do -
 * `protectionFp`'s school tables and `STAT_MAGIC_RESIST` - and `damage.ts` may
 * not reach for either without closing a cycle.
 *
 * Two rules in it are deliberate and neither is obvious.
 *
 * **Resistance REDUCES rather than rolls.** Everywhere else in this file
 * `magicResist` is a chance to shrug a spell off entirely (`resisted`). A blow
 * is not a discrete event you either take or do not: a Ironbeard carries 40
 * per cent innate, and an all-or-nothing reading would make two blows in five
 * from a charged tier-7 do literally nothing. Against one spell it is a
 * chance; against a stream of blows it is a fraction. The classic splits the two the
 * same way - resistance rolls, elemental protection divides.
 *
 * **Magic immunity does NOT apply.** `MAGIC_IMMUNITY` and `SPELL_IMMUNITY` are
 * about spells, and seven creatures carry them including the Umbral Wyrm at
 * level five. Honouring them here would make those creatures immune to an
 * entire unit's ATTACKS - a hard counter rather than a trade, and one that
 * would read as a bug from either side of it. A charged blow is still a blow.
 */
/**
 * The elemental enchantments on one stack, both halves in PERCENT.
 *
 * The conversion lives here rather than in a caller because the two stats are
 * NOT in the same units and nothing about their names says so: `STRIKE_STAT`
 * carries a plain percentage (an enchanted weapon's 15) while `RESIST_STAT`
 * carries fixed point at `FP_ONE` (a Protection spell's 500 means 50%). A
 * reader that treated them alike would misreport one of them by a factor of
 * ten, and the battle info card is exactly such a reader.
 *
 * `protectionFp` already folds in `RESIST_STAT.all`, so a blanket ward shows
 * against every school - which is what it actually does.
 */
export interface ElementalWard {
  readonly school: SpellSchool;
  /** Extra damage this stack's blows carry, as a percentage. */
  readonly strikePct: number;
  /** How much of that school's damage it shrugs off, as a percentage. */
  readonly protectionPct: number;
}

export function elementalWards(s: BattleState, slot: number): readonly ElementalWard[] {
  const out: ElementalWard[] = [];
  for (const school of SPELL_SCHOOLS) {
    const strikePct = resolveStat(0, modsWithStat(s.stacks.mods[slot]!, STRIKE_STAT[school])).value;
    const protectionPct = Math.round((protectionFp(s, slot, school) * 100) / FP_ONE);
    if (strikePct === 0 && protectionPct === 0) continue;
    out.push({ school, strikePct, protectionPct });
  }
  return out;
}

export function magicStrikeFp(
  s: BattleState, content: ContentRegistry, attacker: number, target: number,
  ab: Extract<CreatureAbility, { kind: "MAGIC_ATTACK" }> | null,
): number {
  // Two sources, summed: the charged blow's own bonus, and any enchantment on
  // the attacker (`STRIKE_STAT`, which an enchant-weapon spell installs and an
  // artifact or skill could). A stat rather than a second ability so that the
  // engine has one place to read and content has several ways to grant it.
  let bonusPct = ab === null ? 0 : ab.bonusPct;
  const schools: SpellSchool[] = ab === null ? [] : [ab.school];
  for (const school of SPELL_SCHOOLS) {
    const v = resolveStat(0, modsWithStat(s.stacks.mods[attacker]!, STRIKE_STAT[school])).value;
    if (v <= 0) continue;
    bonusPct += v;
    if (!schools.includes(school)) schools.push(school);
  }
  if (bonusPct <= 0) return FP_ONE;

  // The target's ward against the elements involved. The BEST of them rather
  // than the sum: two enchantments on one blow should not let a single
  // Protection spell be counted twice against it.
  let reduction = 0;
  for (const school of schools) {
    const p = protectionFp(s, target, school);
    if (p > reduction) reduction = p;
  }
  reduction += clampInt(magicResistPct(s, content, target), 0, 100) * (FP_ONE / 100);
  // Capped, and this is the floor that keeps the ability worth carrying: a
  // Ironbeard's 40 plus expert Resistance's 15 plus a Protection spell
  // would otherwise approach total immunity by accident, and the carrier would
  // have paid for a blow that does nothing.
  reduction = clampInt(reduction, 0, MAGIC_STRIKE_REDUCTION_CAP_FP);
  const bonus = FP_ONE + bonusPct * (FP_ONE / 100);
  return Math.max(1, Math.round((bonus * (FP_ONE - reduction)) / FP_ONE));
}

/**
 * A stack's chance to shrug off a spell: the MAGIC_RESISTANCE it was born with
 * plus the resistance its army wears (Lodestone Boots and its cousins, and
 * the hero's Resistance skill). The two add.
 *
 * **Returned uncapped.** The 0-100 clamp is the caller's, and deliberately so:
 * `resisted` halves the figure for an attuned enemy BEFORE clamping, so a
 * helper that clamped first would turn a 120 into 50 where the rule gives 60.
 * The battle info panel is the third reader and wants the raw sum to show.
 */
export function magicResistPct(
  s: BattleState, content: ContentRegistry, slot: number,
): number {
  let pct = 0;
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind === "MAGIC_RESISTANCE") pct += a.pct;
  }
  pct += resolveStat(0, modsWithStat(s.stacks.mods[slot]!, STAT_MAGIC_RESIST)).value;
  return pct;
}

/**
 * MAGIC_RESISTANCE, rolled once per target per cast on `battle.spell`. It is a
 * defence against an enemy's magic only: a Dwarf does not shrug off its own
 * hero's Bless.
 */
function resisted(c: Ctx, content: ContentRegistry, slot: number, casterSide: number): boolean {
  if (c.s.stacks.side[slot] === casterSide) return false;
  let pct = magicResistPct(c.s, content, slot);
  if (attunementActive(c.s, content, casterSide as 0 | 1)) pct = Math.floor(pct / 2);
  if (pct <= 0) return false;
  return rollChanceBp(c, "battle.spell", clampInt(pct, 0, 100) * 100);
}

// --------------------------------------------------------------------- damage

/**
 * `perPower * spellPower + flat`, then mastery, then Spellcraft and artifacts,
 * then the target's elemental protection and vulnerability - every one of them
 * a Modifier, and one rounding at the end inside `resolveStat`.
 */
export function spellDamageAt(
  s: BattleState, content: ContentRegistry, def: SpellDef, e: SpellEffectSpec,
  prof: MagicProfile, slot: number, jump = 0,
): number {
  const flat = e.flat ?? 0;
  const base = (e.perPower ?? 0) * prof.spellPower + flat;
  const mods: Modifier[] = [];
  // A chaining bolt is weaker at every jump. The share is compounded here into
  // one `mulFp` rather than applied per jump, so the whole formula still rounds
  // exactly once inside `resolveStat` (D4).
  const chain = def.chain;
  if (chain !== undefined && jump > 0) {
    let keep = FP_ONE;
    for (let i = 0; i < jump; i++) keep = mulFp(keep, chain.falloffFp);
    mods.push({ source: SRC_CHAIN, stat: "spellDamage", op: "mulFp", value: keep });
  }
  const step = MASTERY_FLAT_STEP[prof.mastery] ?? 0;
  if (step > 0 && flat > 0) {
    mods.push({ source: SRC_MASTERY, stat: "spellDamage", op: "add", value: flat * step });
  }
  for (const m of prof.mods) if (m.stat === "spellDamage") mods.push(m);
  for (const m of specialtyMods(prof, def)) mods.push(m);
  const protect = protectionFp(s, slot, def.school);
  if (protect > 0) {
    mods.push({ source: SRC_PROTECTION, stat: "spellDamage", op: "mulFp", value: FP_ONE - protect });
  }
  const vul = vulnerabilityFp(content, s, slot, def);
  if (vul !== FP_ONE) {
    mods.push({ source: SRC_VULNERABLE, stat: "spellDamage", op: "mulFp", value: vul });
  }
  const v = resolveStat(base, mods).value;
  return v < 0 ? 0 : v;
}

/**
 * The caster's specialty in *this* spell, re-keyed onto `spellDamage` so it
 * lands in the same bracket Spellcraft does. A hero carries at most one, and a
 * hero specialised in another spell contributes nothing here.
 */
function specialtyMods(prof: MagicProfile, def: SpellDef): readonly Modifier[] {
  const spell = spellSpecialtyStat(def.id as string);
  const school = schoolDamageStat(def.school);
  const out: Modifier[] = [];
  for (const m of prof.mods) {
    if (m.stat === spell || m.stat === school) out.push({ ...m, stat: "spellDamage" });
  }
  return out;
}

function spellRestoreAt(e: SpellEffectSpec, prof: MagicProfile, def: SpellDef): number {
  const flat = e.flat ?? 0;
  const base = (e.perPower ?? 0) * prof.spellPower + flat;
  const step = MASTERY_FLAT_STEP[prof.mastery] ?? 0;
  const mods: Modifier[] =
    step > 0 && flat > 0
      ? [{ source: SRC_MASTERY, stat: "spellDamage", op: "add", value: flat * step }]
      : [];
  for (const m of specialtyMods(prof, def)) mods.push(m);
  const v = resolveStat(base, mods).value;
  return v < 0 ? 0 : v;
}

// ------------------------------------------------------------------ targeting

export interface CastTargets {
  /** Stack slots the spell lands on, ascending. Empty for NONE and SELF. */
  readonly stacks: readonly number[];
  /** The hex the effect is centred on, or -1. Carried into the event. */
  readonly hex: number;
}

const NB: number[] = new Array(6).fill(0);
const HEXES: number[] = new Array(2).fill(0);

function occupies(s: BattleState, slot: number, hex: number): boolean {
  const k = stackHexes(s.stacks, slot, HEXES);
  for (let m = 0; m < k; m++) if (HEXES[m] === hex) return true;
  return false;
}

/** The hex and its six neighbours, in `hexNeighbours` order. */
export function areaHexes(centre: number, out: number[]): number {
  out[0] = centre;
  const k = hexNeighbours(centre, NB);
  for (let i = 0; i < k; i++) out[i + 1] = NB[i]!;
  return k + 1;
}

function massTargets(
  s: BattleState, content: ContentRegistry, def: SpellDef, casterSide: number, which: "ally" | "enemy" | "any",
): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.stacks.n; i++) {
    if (!isAlive(s.stacks, i)) continue;
    const same = s.stacks.side[i] === casterSide;
    if (which === "ally" && !same) continue;
    if (which === "enemy" && same) continue;
    if (spellImmune(s, content, i, def)) continue;
    out.push(i);
  }
  return out;
}

/**
 * Where a chaining bolt goes after its first victim.
 *
 * Each jump lands on the nearest stack the bolt has not touched yet, measured
 * from the stack it is leaving rather than from the caster. Sides are not
 * consulted: the classic's Forked Lightning walks into your own line if your own line is
 * what is nearest, which is the whole reason it is cast at the flank. Ties
 * break on the lower slot, so the walk is deterministic and costs no roll.
 */
function chainWalk(
  s: BattleState, content: ContentRegistry, def: SpellDef, first: number, limit: number,
): number[] {
  const st = s.stacks;
  const out = [first];
  while (out.length < limit) {
    const from = st.hex[out[out.length - 1]!]!;
    let best = -1;
    let bestDist = Number.MAX_SAFE_INTEGER;
    for (let i = 0; i < st.n; i++) {
      if (!isAlive(st, i) || out.includes(i)) continue;
      if (spellImmune(s, content, i, def)) continue;
      const d = hexDistance(from, st.hex[i]!);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    if (best < 0) break;
    out.push(best);
  }
  return out;
}

/**
 * The targeting a cast actually uses. Expert mastery turns the classic buffs and
 * debuffs content marks `massAtExpert` into their mass versions, which is a
 * change of *targeting* and nothing else - the effects, the duration and the
 * mana cost are already mastery-aware through `MASTERY_DURATION_BONUS` and the
 * per-mastery cost table, so Mass Bless needs no second spell def.
 */
function effectiveTargeting(def: SpellDef, mastery: number): SpellTargeting {
  if (def.massAtExpert !== true || mastery < 3) return def.targeting;
  if (def.targeting === "SINGLE_ALLY") return "ALL_ALLIES";
  if (def.targeting === "SINGLE_ENEMY") return "ALL_ENEMIES";
  return def.targeting;
}

/**
 * The stacks and hex a cast lands on.
 *
 * `mastery` decides whether a `massAtExpert` spell spreads. Omitted, it is read
 * out of the side's own magic profile, which is right for every hero cast and
 * for an outside scorer holding only the state; a creature caster has no school
 * mastery at all, so `validateCreatureCast` passes 0 rather than borrowing its
 * hero's.
 */
export function resolveTargets(
  s: BattleState, content: ContentRegistry, def: SpellDef, casterSide: number, target: number,
  mastery?: number,
): Result<CastTargets, Reason> {
  const st = s.stacks;
  const lvl = mastery ?? masteryFor(sideMagicMods(s, casterSide), def.school);
  const how = effectiveTargeting(def, lvl);
  const single = (which: "ally" | "enemy" | "any"): Result<CastTargets, Reason> => {
    if (!Number.isInteger(target) || target < 0 || target >= st.n) {
      return Err({ code: "TARGET_INVALID" });
    }
    if (!isAlive(st, target)) return Err({ code: "STACK_DEAD", stack: target });
    const same = st.side[target] === casterSide;
    if (which === "ally" && !same) return Err({ code: "TARGET_INVALID" });
    if (which === "enemy" && same) return Err({ code: "TARGET_INVALID" });
    if (spellImmune(s, content, target, def)) return Err({ code: "TARGET_INVALID" });
    const stacks = def.chain === undefined
      ? [target]
      : chainWalk(s, content, def, target, def.chain.targets);
    return Ok({ stacks, hex: st.hex[target]! });
  };

  switch (how) {
    case "SINGLE_ALLY": return single("ally");
    case "SINGLE_ENEMY": return single("enemy");
    case "SINGLE_ANY": return single("any");
    case "SELF": case "NONE": return Ok({ stacks: [], hex: -1 });
    case "ALL_ALLIES": case "ALL_ENEMIES": case "ALL": {
      // `how`, not `def.targeting`: a Mass Bless reached this arm because expert
      // mastery widened it, and reading the authored value here would hand the
      // whole battlefield a buff meant for one side.
      const which = how === "ALL_ALLIES" ? "ally" : how === "ALL_ENEMIES" ? "enemy" : "any";
      const stacks = massTargets(s, content, def, casterSide, which);
      if (stacks.length === 0) return Err({ code: "TARGET_INVALID" });
      return Ok({ stacks, hex: stacks[0] === undefined ? -1 : st.hex[stacks[0]]! });
    }
    case "HEX": {
      if (!Number.isInteger(target) || target < 0 || target >= BF_HEXES) {
        return Err({ code: "TARGET_INVALID" });
      }
      return Ok({ stacks: [], hex: target });
    }
    case "AREA": {
      if (!Number.isInteger(target) || target < 0 || target >= BF_HEXES) {
        return Err({ code: "TARGET_INVALID" });
      }
      const area: number[] = new Array(7).fill(-1);
      const k = areaHexes(target, area);
      const stacks: number[] = [];
      let touched = false;
      for (let i = 0; i < st.n; i++) {
        if (!isAlive(st, i)) continue;
        let hit = false;
        for (let m = 0; m < k; m++) if (occupies(s, i, area[m]!)) { hit = true; break; }
        if (!hit) continue;
        touched = true;
        if (spellImmune(s, content, i, def)) continue;
        stacks.push(i);
      }
      // The expansion: an area spell that catches nobody may not be cast at all.
      if (!touched) return Err({ code: "TARGET_INVALID" });
      if (stacks.length === 0) return Err({ code: "TARGET_INVALID" });
      return Ok({ stacks, hex: target });
    }
    case "MAP": return Err({ code: "TARGET_INVALID" });
  }
}

// ------------------------------------------------------------- battle rules
//
// The `ADVENTURE` arm of the effect union, keyed by rule id. Each returns
// whether it actually did something to the stack it was handed.

type RuleFn = (
  c: Ctx, content: ContentRegistry, def: SpellDef, prof: MagicProfile,
  slot: number, hex: number, rounds: number,
  /**
   * The side that cast. Appended rather than threaded through the middle so
   * that every rule written before it keeps its own shorter signature - a
   * JavaScript function may declare fewer parameters than its type does, and
   * only the rules that aim at the *caster's* own side need this one.
   */
  side: 0 | 1,
) => boolean;

function statusMod(
  def: SpellDef, stat: StatKey, op: Modifier["op"], value: number, rounds: number,
): Modifier {
  return {
    source: srcOf(def), stat, op, value,
    stackGroup: def.id as string, stackRule: "highest",
    expires: { kind: "round", rounds },
  };
}

/**
 * Lay `count` hexes of one conjured obstacle, centred on `hex` and spreading
 * along the column - a wall of flame runs across the field, not along it.
 * Existing obstacles are never overwritten: a barrier that ate a wall segment
 * would be a way to un-build a fort.
 */
function conjure(
  c: Ctx, hex: number, kind: ObstacleKind, count: number, integrity: number, power: number,
): boolean {
  if (hex < 0 || hex >= BF_HEXES || count <= 0) return false;
  const taken = new Set<number>(c.s.obstacles.map((o) => o.hex));
  const laid: Obstacle[] = [];
  // Centre first, then alternate above and below, so one hex is the hex asked
  // for and the spread is symmetric and deterministic.
  for (let step = 0; laid.length < count && step < BF_HEIGHT; step++) {
    const row = hexRow(hex) + (step === 0 ? 0 : (step & 1) === 1 ? (step + 1) >> 1 : -(step >> 1));
    if (row < 0 || row >= BF_HEIGHT) continue;
    const h = hexIndex(hexCol(hex), row);
    if (taken.has(h)) continue;
    taken.add(h);
    laid.push(power > 0
      ? { kind, hex: h, integrity, power, art: "" }
      : { kind, hex: h, integrity, art: "" });
  }
  if (laid.length === 0) return false;
  jSetField(c.j, c.s as unknown as object, "obstacles", [...c.s.obstacles, ...laid]);
  return true;
}

/** What a Flame Barrier or a Rune Trap deals: the spell's own DAMAGE effect. */
function fireWallDamage(def: SpellDef, prof: MagicProfile): number {
  for (const e of def.effects) {
    if (e.kind !== "DAMAGE") continue;
    const flat = e.flat ?? 0;
    const step = MASTERY_FLAT_STEP[prof.mastery] ?? 0;
    const v = (e.perPower ?? 0) * prof.spellPower + flat + flat * step;
    return v < 1 ? 1 : v;
  }
  return 1;
}

/**
 * Enchant weapon: the stack's blows carry an element.
 *
 * Flat rather than scaled by mastery or spell power, unlike `protectionRule`
 * below. The bonus is a share of the stack's OWN damage, so a hero with high
 * spell power would otherwise make a tier-7 army's every swing enormous - the
 * spell is meant to be a steady edge, not a multiplier on somebody else's
 * numbers. Spell power still decides how long it lasts, which is where a
 * strong magic hero's advantage shows.
 */
const ENCHANT_WEAPON_PCT = 15;

function enchantWeaponRule(school: SpellSchool): RuleFn {
  return (c, _content, def, _prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STRIKE_STAT[school], "add", ENCHANT_WEAPON_PCT, rounds));
    return true;
  };
}

function protectionRule(school: SpellSchool): RuleFn {
  return (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, RESIST_STAT[school], "add", PROTECTION_FP[prof.mastery] ?? 0, rounds));
    return true;
  };
}

const BATTLE_RULES: Readonly<Record<string, RuleFn>> = Object.freeze({
  // Bless: the stack always rolls its top damage, which is exactly "raise the
  // floor of damageMin to damageMax" - `resolveStat`'s `max` op, not a flag.
  "rule.damage_roll_max": (c, _content, def, _prof, slot, _hex, rounds) => {
    const st = c.s.stacks;
    const top = resolveStat(st.damageMax[slot]!, modsWithStat(st.mods[slot]!, "damageMax")).value;
    addMod(c, slot, statusMod(def, "damageMin", "max", top, rounds));
    return true;
  },
  // Curse: the mirror image - cap the ceiling at the floor.
  "rule.damage_roll_min": (c, _content, def, _prof, slot, _hex, rounds) => {
    const st = c.s.stacks;
    const low = resolveStat(st.damageMin[slot]!, modsWithStat(st.mods[slot]!, "damageMin")).value;
    addMod(c, slot, statusMod(def, "damageMax", "min", low, rounds));
    return true;
  },
  "rule.blind": (c, _content, def, prof, slot, _hex, rounds) => {
    setFlags(c, slot, F_BLINDED);
    // The description is the rule: "cannot act until it is struck, and the
    // blow that wakes it is halved. Expert mastery removes the retaliation".
    // The modifier is what makes it last past one lost turn, and its value
    // carries the mastery for the two rules the waking blow reads.
    addMod(c, slot, statusMod(def, STAT_BLIND, "max", prof.mastery + 1, rounds));
    return true;
  },
  "rule.weapon.air": enchantWeaponRule("air"),
  "rule.weapon.earth": enchantWeaponRule("earth"),
  "rule.weapon.fire": enchantWeaponRule("fire"),
  "rule.weapon.water": enchantWeaponRule("water"),
  "rule.protection.air": protectionRule("air"),
  "rule.protection.earth": protectionRule("earth"),
  "rule.protection.fire": protectionRule("fire"),
  "rule.protection.water": protectionRule("water"),
  "rule.null_ward": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_ANTI_MAGIC, "max", ANTI_MAGIC_LEVEL[prof.mastery] ?? 3, rounds));
    return true;
  },
  "rule.melee_damage_reduction": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_DAMAGE_TAKEN, "add", SHIELD_FP[prof.mastery] ?? 0, rounds));
    return true;
  },
  // Vulnerability, the mirror of Shield and deliberately the same stat: one
  // number decides how hard a stack is to hurt, and a second "extra damage
  // taken" key would be a second answer to the same question.
  "rule.damage_amplification": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_DAMAGE_TAKEN, "add", -(VULNERABILITY_FP[prof.mastery] ?? 0), rounds));
    return true;
  },
  "rule.ranged_damage_reduction": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_DAMAGE_TAKEN, "add", AIR_SHIELD_FP[prof.mastery] ?? 0, rounds));
    return true;
  },
  "rule.befuddle": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_DAMAGE_RANGED, "add", -(FORGETFULNESS_FP[prof.mastery] ?? 0), rounds));
    return true;
  },
  // Frenzy trades the stack's whole defence for attack, so the numbers are read
  // at cast time and the defence is `set` to zero rather than subtracted.
  "rule.frenzy": (c, _content, def, prof, slot, _hex, rounds) => {
    const gain = mulFp(effectiveDefense(c.s, slot), FRENZY_FP[prof.mastery] ?? FP_ONE);
    addMod(c, slot, statusMod(def, "attack", "add", gain, rounds));
    addMod(c, slot, statusMod(def, "defense", "set", 0, rounds));
    return true;
  },
  // ---- the conjured obstacles. Each is "put this on some hexes"; where the
  // hexes are is the caster's choice and how many is his mastery.
  "obstacle.flame_barrier": (c, _content, def, prof, _slot, hex) =>
    conjure(c, hex, "firewall", FIRE_WALL_HEXES[prof.mastery] ?? 3,
      BARRIER_ROUNDS[prof.mastery] ?? 2, fireWallDamage(def, prof)),
  "obstacle.rune_trap": (c, _content, def, prof, _slot, hex) =>
    // A mine waits: its integrity is the one bite it has, not a countdown.
    conjure(c, hex, "landmine", LAND_MINE_HEXES[prof.mastery] ?? 1, 1,
      fireWallDamage(def, prof)),
  "obstacle.sinking_sand": (c, _content, _def, prof, _slot, hex) =>
    conjure(c, hex, "sinking_sand", QUICKSAND_PATCHES[prof.mastery] ?? 1, 1, 0),
  "obstacle.ward_wall": (c, _content, _def, prof, _slot, hex, rounds) =>
    conjure(c, hex, "forcefield", FORCE_FIELD_HEXES[prof.mastery] ?? 2,
      rounds > 0 ? rounds : BARRIER_ROUNDS[prof.mastery] ?? 2, 0),
  /**
   * Clear Path. Basic mastery shifts natural rubble; expert also unmakes
   * a conjured barrier, which is the difference the spell's own description
   * draws and the reason it is worth carrying against an Earth mage.
   */
  "obstacle.remove": (c, _content, _def, prof, _slot, hex) => {
    const keep: Obstacle[] = [];
    let removed = false;
    for (const o of c.s.obstacles) {
      const natural = o.kind === "rock" || o.kind === "tree";
      const canGo = natural || (prof.mastery >= 3 && isConjured(o));
      if (o.hex === hex && canGo && !removed) { removed = true; continue; }
      keep.push(o);
    }
    if (!removed) return false;
    jSetField(c.j, c.s as unknown as object, "obstacles", keep);
    return true;
  },

  // ---- the rules that are a flag or a scoped number on one stack
  "rule.extra_retaliation": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_EXTRA_RETALIATION, "add",
      COUNTERSTRIKE_EXTRA[prof.mastery] ?? 1, rounds));
    return true;
  },
  "rule.ember_mantle": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_FIRE_SHIELD, "max",
      FIRE_SHIELD_PCT[prof.mastery] ?? 20, rounds));
    return true;
  },
  "rule.spell_mirror": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_MAGIC_MIRROR, "max",
      MAGIC_MIRROR_BP[prof.mastery] ?? 2000, rounds));
    return true;
  },
  /**
   * Slayer. The classic names its quarry - great_maws, manyheads, dragons, then angels and
   * devils, then thunder_colossi - and mastery lengthens the list. A list of creature
   * ids in rule code is exactly what D9 forbids, so the list is expressed as
   * the fact those creatures share: they are the biggest things on the field.
   * The tier the bonus starts biting at comes down as mastery goes up.
   */
  "rule.slayer": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_SLAYER_ATTACK, "max", SLAYER_ATTACK, rounds));
    addMod(c, slot, statusMod(def, STAT_SLAYER_TIER, "min", SLAYER_TIER[prof.mastery] ?? 7, rounds));
    return true;
  },
  /**
   * Blood Rage. The flag is what `chooseBattleCommand` and the turn machine read;
   * a maddened stack attacks whatever is nearest, friend or foe.
   */
  "rule.blood_rage": (c, _content, def, _prof, slot, _hex, rounds) => {
    // The flag is what `settle` reads to take the stack's turn away from its
    // owner; the status is how long the madness lasts. It used to set only the
    // flag, and nothing read that flag, so the spell was inert.
    addMod(c, slot, statusMod(def, STAT_BERSERK, "max", 1, rounds));
    setFlags(c, slot, F_BERSERK);
    return true;
  },
  /**
   * Enthrall. The stack changes sides outright - its `side` column is written -
   * so targeting, retaliation and the turn order are all correct without a
   * single rule knowing the spell exists. `F_HYPNOTIZED` is only how the kernel
   * remembers to hand it back when the spell lapses.
   *
   * The classic gates it on the target's remaining hit points, which is what stops it
   * being an instant win against the enemy's best stack.
   */
  "rule.enthrall": (c, _content, def, prof, slot, _hex, rounds) => {
    const st = c.s.stacks;
    const pool = (st.count[slot]! - 1) * st.maxHp[slot]! + st.topHp[slot]!;
    const cap = (HYPNOSIS_HP_PER_POWER[prof.mastery] ?? 10) * prof.spellPower;
    if (pool > cap) return false;
    if ((st.flags[slot]! & F_HYPNOTIZED) !== 0) return false;
    addMod(c, slot, statusMod(def, STAT_HYPNOSIS, "max", 1, rounds));
    setFlags(c, slot, F_HYPNOTIZED);
    jSetI32(c.j, st.side, slot, st.side[slot] === 0 ? 1 : 0);
    jSetI32(c.j, st.facing, slot, -st.facing[slot]!);
    return true;
  },
  /**
   * Hornet Cloud. The target keeps its turn and loses its place in the round: it
   * is written into the wait queue, behind everything that chose to wait.
   *
   * `waited` is the whole record, and `F_WAITED` is deliberately NOT set: that
   * bit is the stack's own choice, and the difference is load-bearing at both
   * ends. `legal.ts` reads `waited`, so a swarmed stack is not offered the WAIT
   * it never took; `applyShieldWall` reads `F_WAITED`, so a perk that pays for
   * standing still does not pay a stack that was driven back.
   *
   * Nothing happens to a stack that has already acted or is acting now: the
   * queue behind `turnIndex` is the only part `reorderForWait` may touch, and
   * pushing the active stack back would be a way to take a turn away outright
   * rather than to delay one.
   */
  /**
   * Silence. The other hero may not cast at all while it stands.
   *
   * Four sealed schools rather than a new "may not cast" flag, because the
   * sealed-school stat is the one mechanism in the game that is *one-sided*:
   * `sideMagicMods` reads it off the caster's own stacks and binds only the
   * opponent, which is exactly the shape a silence needs and exactly the shape
   * the field-wide level cap does not have (it would gag both heroes).
   *
   * It follows that a spell of school "all" is not silenced - `validateCast`
   * exempts them by construction, since they belong to none of the four - so
   * Arcane Dart and its kin still answer. That is the spell's own hole and the
   * description says so; closing it would mean a second mechanism.
   */
  "rule.silence": (c, _content, _def, _prof, _slot, _hex, rounds, side) => {
    let laid = false;
    const st = c.s.stacks;
    for (let i = 0; i < st.n; i++) {
      if (st.side[i] !== side || !isAlive(st, i)) continue;
      for (const school of SEALED_SCHOOLS) {
        // Under the magic prefix, and NOT under the spell's own id: the seal is
        // read back through `sideMagicMods`, which collects nothing else. A
        // seal signed `spell.silence` sits on the right stacks, survives every
        // round, and is never looked at - which is what it did until this test
        // asked the validator instead of the modifier list.
        addMod(c, i, {
          source: asModifierSrcId(`${MAGIC_SRC_PREFIX}silence.${school}`),
          stat: sealedSchoolStat(school), op: "max", value: 1,
          expires: { kind: "round", rounds },
        });
      }
      laid = true;
    }
    return laid;
  },
  /**
   * Mana Drain. Spell points off the other hero and into the caster's own pool.
   *
   * The only rule that touches a hero's mana mid-battle, so it is written where
   * every other magic rule is rather than in `execute.ts`: what it drains is
   * decided by the caster's power, and the profile that carries power is here.
   */
  "rule.mana_drain": (c, _content, _def, prof, _slot, _hex, _rounds, side) => {
    const foe = side === 0 ? 1 : 0;
    const from = c.s.heroes[foe];
    const to = c.s.heroes[side];
    // A wandering army has no hero and therefore no pool; the cast is refused
    // rather than silently drawing on nothing (`castSpell` keeps the mana).
    if (from.id === null || to.id === null) return false;
    const want = MANA_LEECH_PER_POWER * prof.spellPower;
    const took = Math.min(want, from.mana);
    if (took <= 0) return false;
    jSetField(c.j, from as unknown as object, "mana", from.mana - took);
    jSetField(c.j, to as unknown as object, "mana", to.mana + took);
    return true;
  },
  /**
   * Goad. The stack throws down its bow and comes on.
   *
   * Spending the ammunition rather than setting a "may not shoot" bit: the
   * shot count is already the one gate every shooting path consults - the
   * enumerator, the execute arm and the preemptive strike all test it - and a
   * second way to say the same thing is a second thing to keep in step.
   */
  "rule.goad": (c, _content, _def, _prof, slot) => {
    const st = c.s.stacks;
    if (st.shots[slot]! <= 0) return false;
    jSetI32(c.j, st.shots, slot, 0);
    return true;
  },
  /**
   * Petrify. The stack is stone: it cannot act, and it is very hard to break.
   *
   * Both halves are borrowed rather than invented - `F_BLINDED` is already
   * "loses its turn until struck" and `STAT_DAMAGE_TAKEN` is already the one
   * answer to "how hard is this to hurt" - which is what keeps the waking blow
   * behaving the way a blinded stack's does.
   */
  "rule.petrify": (c, _content, _def, _prof, slot, _hex, rounds) => {
    if ((c.s.stacks.flags[slot]! & F_PETRIFIED) !== 0) return false;
    setFlags(c, slot, F_PETRIFIED);
    // Under the ABILITY's own source, not the spell's: `wakeOnHit` breaks the
    // stone by removing `SRC_PETRIFY`, and a spell that signed its own work
    // would leave the reduction standing on a stack that is no longer stone.
    addMod(c, slot, {
      source: SRC_PETRIFY, stat: STAT_DAMAGE_TAKEN, op: "add", value: PETRIFY_FP,
      stackGroup: "petrify", stackRule: "first",
      expires: { kind: "round", rounds },
    });
    return true;
  },
  /**
   * Still Moment. The stack acts again in this same round.
   *
   * The mirror of `rule.delay`: that one puts a stack back in the queue, this
   * one takes its spent turn back. It refuses the stack that is acting right
   * now for the same reason Hornet Cloud refuses it - the active turn is being
   * resolved, and handing it back mid-resolution is not a second turn, it is
   * an undo.
   */
  "rule.still_moment": (c, _content, _def, _prof, slot) => {
    const s = c.s;
    if (slot === activeStack(s)) return false;
    if ((s.stacks.flags[slot]! & F_ACTED) === 0) return false;
    clearFlags(c, slot, F_ACTED);
    return true;
  },
  "rule.delay": (c, _content, _def, _prof, slot) => {
    const s = c.s;
    if (slot === activeStack(s)) return false;
    if ((s.stacks.flags[slot]! & F_ACTED) !== 0) return false;
    if (s.waited[slot] === 1) return false;
    jSetI32(c.j, s.waited, slot, 1);
    reorderForWait(s, c.j);
    return true;
  },
  /**
   * Regeneration. A number on the stack that `beginTurn` pays out, so the heal
   * lands at the start of each of the target's own turns rather than at a round
   * boundary - a stack that acts late in the round is mended late, which is
   * what "at the start of its turn" means and is worth a tempo point.
   */
  "rule.regeneration": (c, _content, def, prof, slot, _hex, rounds) => {
    const heal = REGEN_FLAT + REGEN_PER_POWER * prof.spellPower;
    addMod(c, slot, statusMod(def, STAT_REGEN, "max", heal, rounds));
    return true;
  },
  /**
   * Vampirism. The same key the LIFE_DRAIN ability pays out through, so the
   * spell and the ability are one rule read in one place; a Nightfeeder under this
   * spell does not drain twice, it drains at the better of the two shares.
   */
  "rule.life_drain": (c, _content, def, prof, slot, _hex, rounds) => {
    addMod(c, slot, statusMod(def, STAT_LIFE_DRAIN, "max", VAMPIRISM_BP[prof.mastery] ?? 3000, rounds));
    return true;
  },
  /**
   * Holy Retribution. The bill for what this stack has killed, which the store
   * has been keeping all along: `killed` is per battle and per stack, and was
   * already there for the after-action report.
   *
   * Returns false against a stack that has killed nothing, so the cast is
   * refused rather than charged - `resolveTargets` cannot know this and the
   * mana is spent by the time a rule runs, but a rule that returns false at
   * least leaves the event honest about having done nothing.
   */
  "rule.holy_retribution": (c, _content, _def, prof, slot) => {
    const st = c.s.stacks;
    const owed = st.killed[slot]!;
    if (owed <= 0) return false;
    const raw = owed * VENGEANCE_PER_KILL_PER_POWER * prof.spellPower;
    const cap = VENGEANCE_CAP_FLAT + VENGEANCE_CAP_PER_POWER * prof.spellPower;
    const dmg = raw > cap ? cap : raw;
    if (dmg <= 0) return false;
    const r = damageStack(c, slot, dmg);
    c.events.push({
      type: "BattleAttacked", attacker: slot, target: slot,
      damage: r.dealt, killed: r.killed, ranged: true, retaliation: false,
    });
    return r.dealt > 0;
  },
  // Earthquake is the only rule with no stack target: it works on the siege
  // works, exactly as BATTLE_CATAPULT does.
  "siege.earthquake": (c, _content, _def, prof) => {
    const s = c.s;
    if (s.siege === null || !s.siege.active) return false;
    const targets: number[] = [];
    for (let i = 0; i < s.obstacles.length; i++) {
      const o = s.obstacles[i]!;
      if ((o.kind === "wall" || o.kind === "gate") && o.integrity > 0) targets.push(i);
    }
    let left = EARTHQUAKE_SECTIONS[prof.mastery] ?? 2;
    let any = false;
    while (left > 0 && targets.length > 0) {
      const pick = rollBounded(c, "battle.spell", targets.length);
      const idx = targets[pick]!;
      targets.splice(pick, 1);
      const o = s.obstacles[idx]!;
      const integrity = o.integrity - 1;
      jSetField(c.j, o as unknown as object, "integrity", integrity);
      c.events.push({ type: "BattleWallHit", segment: idx, integrity });
      if (o.kind === "gate" && integrity <= 0) {
        jSetField(c.j, s.siege as unknown as object, "gateOpen", true);
      }
      any = true;
      left--;
    }
    return any;
  },
});

/** Rules that cannot fire outside a siege, so legality never offers them there. */
const SIEGE_ONLY_RULES: readonly string[] = Object.freeze(["siege.earthquake"]);

/**
 * Rules that work on the battlefield rather than on a stack. Everything else
 * needs a live slot, and `resolveCast` runs each effect once with slot -1 when
 * every target resisted - so a stack rule handed -1 would read `stacks.mods[-1]`
 * and throw. Curse landing on a Dwarf who shrugged it off is the whole repro.
 */
const SLOTLESS_RULES: readonly string[] = Object.freeze([
  "siege.earthquake",
  "obstacle.flame_barrier", "obstacle.rune_trap", "obstacle.sinking_sand",
  "obstacle.ward_wall", "obstacle.remove",
  // Aimed at a hero rather than at a stack: there is no target slot to be
  // alive, and requiring one would make them uncastable on an empty flank.
  "rule.silence", "rule.mana_drain",
]);

// ---------------------------------------------------------------- resolution

/** Strip every modifier a spell put on a stack, and the statuses spells set. */
function dispelStack(c: Ctx, slot: number): boolean {
  const cur = c.s.stacks.mods[slot]!;
  const keep: Modifier[] = [];
  for (const m of cur) if (!(m.source as string).startsWith(SPELL_SRC_PREFIX)) keep.push(m);
  let changed = false;
  if (keep.length !== cur.length) {
    jSetField(c.j, c.s.stacks.mods as unknown as object, slot, keep);
    changed = true;
  }
  const held = c.s.stacks.flags[slot]! & (F_BLINDED | F_PETRIFIED);
  if (held !== 0) {
    jSetI32(c.j, c.s.stacks.flags, slot, c.s.stacks.flags[slot]! & ~held);
    changed = true;
  }
  return changed;
}

/** One effect against one stack. The single switch over the closed union. */
function applyEffect(
  c: Ctx, content: ContentRegistry, def: SpellDef, e: SpellEffectSpec,
  prof: MagicProfile, casterStack: number, slot: number, hex: number, jump: number,
): boolean {
  const st = c.s.stacks;
  switch (e.kind) {
    case "DAMAGE": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      const dmg = spellDamageAt(c.s, content, def, e, prof, slot, jump);
      const r = damageStack(c, slot, dmg);
      if (r.died) rallyOnDeath(c, content, slot);
      // No stack swung, so the blow is reported the way the moat reports its
      // own: attacker and target are the same slot, which the renderer draws as
      // damage landing rather than as a charge.
      const attacker = casterStack >= 0 ? casterStack : slot;
      c.events.push({
        type: "BattleAttacked", attacker, target: slot,
        damage: r.dealt, killed: r.killed, ranged: true, retaliation: false,
      });
      return r.dealt > 0;
    }
    case "HEAL": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      return healStack(c, slot, spellRestoreAt(e, prof, def), st.count[slot]!) > 0;
    }
    case "RESURRECT": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      if (raisingBarred(c.s)) return false;
      const hp = spellRestoreAt(e, prof, def);
      const maxHp = st.maxHp[slot]! < 1 ? 1 : st.maxHp[slot]!;
      // A stack is raised back toward what it marched in with, never past it:
      // The classic caps Resurrection and Raise Fallen at the original size. A stack
      // with no start marker (a battle saved before every stack was stamped)
      // keeps the old open ceiling rather than refusing the cast.
      const born = startCount(c.s, slot);
      const grow = st.count[slot]! + Math.ceil(hp / maxHp);
      const room = born > 0 ? Math.min(born, grow) : grow;
      return healStack(c, slot, hp, room) > 0;
    }
    case "MODIFIER": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      const list = e.modifiers ?? [];
      if (list.length === 0) return false;
      const rounds = spellDurationRounds(e, prof);
      for (const m of list) {
        addMod(c, slot, {
          ...amplifyBySpecialty(m, prof.specialtyFp), expires: { kind: "round", rounds },
        });
      }
      return true;
    }
    case "DISPEL": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      return dispelStack(c, slot);
    }
    case "ADVENTURE": {
      const rule = e.adventure === undefined ? "" : e.adventure;
      const fn = BATTLE_RULES[rule];
      if (fn === undefined) return false;
      if (slot < 0) { if (!SLOTLESS_RULES.includes(rule)) return false; }
      else if (!isAlive(st, slot)) return false;
      return fn(
        c, content, def, prof, slot, hex, spellDurationRounds(e, prof),
        casterSide(c, casterStack, slot));
    }
    /**
     * Summon Elemental. The count is spell power times the effect's own
     * `perPower`, and the expert bonus is a second SUMMON with a bigger one -
     * so mastery is data and this arm has no mastery in it.
     */
    case "SUMMON": {
      if (e.summon === undefined) return false;
      const side = casterSide(c, casterStack, slot);
      const count = (e.perPower ?? 1) * prof.spellPower;
      if (count <= 0) return false;
      const summoned = content.creatures.get(e.summon);
      if (summoned === undefined) return false;
      const twoHex = summoned.abilities.some((a) => a.kind === "TWO_HEX");
      const at = freeHexFor(c.s, side, summonAnchor(c.s, side), twoHex);
      if (at < 0) return false;
      const made = addStack(c, content, { def: e.summon, side, count, hex: at });
      if (made < 0) return false;
      c.events.push({ type: "BattleStackSummoned", stack: made, creature: e.summon, count });
      return true;
    }
    /**
     * Clone. A phantom with the target's own numbers that fights and is never
     * folded back into an army. It goes when the spell lapses, which the round
     * tick reads off `F_CLONE` and the modifier this leaves on it.
     */
    case "CLONE": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      const side = st.side[slot] === 1 ? 1 : 0;
      const twoHex = (st.flags[slot]! & F_TWO_HEX) !== 0;
      const at = freeHexFor(c.s, side, st.hex[slot]!, twoHex);
      if (at < 0) return false;
      const rounds = spellDurationRounds(e, prof);
      const made = addStack(c, content, {
        def: st.def[slot]!, side, count: st.count[slot]!, hex: at,
        extraFlags: F_CLONE,
        stats: {
          attack: st.attack[slot]!, defense: st.defense[slot]!,
          damageMin: st.damageMin[slot]!, damageMax: st.damageMax[slot]!,
          maxHp: st.maxHp[slot]!, morale: st.morale[slot]!, luck: st.luck[slot]!,
        },
      });
      if (made < 0) return false;
      addMod(c, made, statusMod(def, STAT_CLONE, "max", 1, rounds));
      c.events.push({ type: "BattleStackSummoned", stack: made, creature: st.def[slot]!, count: st.count[slot]! });
      return true;
    }
    /**
     * Sacrifice. The target is the stack being *restored*; the stack given up
     * is chosen by the kernel, because a `BattleCommand` carries one target and
     * asking the player for a second would be a new command shape for one
     * spell. It takes the cheapest living stack that is not the beneficiary,
     * which is the choice a player makes anyway.
     */
    case "SACRIFICE": {
      if (slot < 0 || !isAlive(st, slot)) return false;
      const victim = cheapestAlly(c, content, slot);
      if (victim < 0) return false;
      const given = st.count[victim]!;
      const hp = given * (st.maxHp[victim]! + (e.perPower ?? 0) * prof.spellPower);
      const killed = damageStack(c, victim, (st.count[victim]! - 1) * st.maxHp[victim]! + st.topHp[victim]!);
      c.events.push({
        type: "BattleAttacked", attacker: victim, target: victim,
        damage: killed.dealt, killed: killed.killed, ranged: false, retaliation: false,
      });
      const maxHp = st.maxHp[slot]! < 1 ? 1 : st.maxHp[slot]!;
      return healStack(c, slot, hp, st.count[slot]! + Math.ceil(hp / maxHp)) > 0;
    }
    /**
     * Teleport. `HEX` targeting, and the stack that moves is the one whose turn
     * the hero is spending: the classic casts during a stack's turn, and "the stack that
     * was about to act appears somewhere else" is both the natural reading and
     * the only one a single-target command can express.
     */
    case "TELEPORT": {
      if (casterStack >= 0) return false;
      const mover = activeStack(c.s);
      if (mover < 0 || !isAlive(st, mover)) return false;
      if (hex < 0 || hex >= BF_HEXES) return false;
      const occ = newOccupancy(c.s);
      if (!canOccupy(c.s, occ, mover, hex)) return false;
      setHex(c, mover, hex);
      c.events.push({ type: "BattleStackMoved", stack: mover, path: [hex] });
      return true;
    }
  }
}

/** Whose spell this is: a creature's own side, or the hero whose turn it is. */
function casterSide(c: Ctx, casterStack: number, slot: number): 0 | 1 {
  if (casterStack >= 0) return c.s.stacks.side[casterStack] === 1 ? 1 : 0;
  const active = activeStack(c.s);
  if (active >= 0) return c.s.stacks.side[active] === 1 ? 1 : 0;
  return slot >= 0 && c.s.stacks.side[slot] === 1 ? 1 : 0;
}

/** Where a summon likes to appear: beside the side's own front rank. */
function summonAnchor(s: BattleState, side: 0 | 1): number {
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (st.side[i] === side && isAlive(st, i)) return st.hex[i]!;
  }
  return -1;
}

/** The cheapest living stack on a side that is not `except`, by aiValue. */
function cheapestAlly(c: Ctx, content: ContentRegistry, except: number): number {
  const st = c.s.stacks;
  const side = st.side[except]!;
  let best = -1;
  let bestValue = Number.MAX_SAFE_INTEGER;
  for (let i = 0; i < st.n; i++) {
    if (i === except || st.side[i] !== side || !isAlive(st, i)) continue;
    if (isWarMachine(st, i)) continue;
    const v = (content.creatures.get(st.def[i]!)?.aiValue ?? 0) * st.count[i]!;
    if (v < bestValue) { bestValue = v; best = i; }
  }
  return best;
}

/** Why this spell cannot be resolved by this kernel, or null when it can. */
export function unsupportedEffect(def: SpellDef, mastery: number, siege: boolean): string | null {
  const check = (e: SpellEffectSpec): string | null => {
    if (!SUPPORTED_EFFECTS.includes(e.kind)) return e.kind;
    if (e.kind !== "ADVENTURE") return null;
    const rule = e.adventure === undefined ? "" : e.adventure;
    if (BATTLE_RULES[rule] === undefined) return rule;
    if (!siege && SIEGE_ONLY_RULES.includes(rule)) return rule;
    return null;
  };
  for (const e of def.effects) {
    const bad = check(e);
    if (bad !== null) return bad;
  }
  if (mastery >= 3 && def.expertBonus !== undefined) {
    const bad = check(def.expertBonus);
    if (bad !== null) return bad;
  }
  return null;
}

/**
 * Land a validated cast. Resistance is rolled first, for every target in slot
 * order, so the affected list the event carries is the list the effects run
 * against and the renderer never flashes a stack that shrugged the spell off.
 */
function resolveCast(
  c: Ctx, content: ContentRegistry, def: SpellDef, prof: MagicProfile,
  side: 0 | 1, casterStack: number, targets: CastTargets,
): void {
  const affected: number[] = [];
  for (const slot of targets.stacks) {
    if (!isAlive(c.s.stacks, slot)) continue;
    if (resisted(c, content, slot, side)) continue;
    // Spell Mirror rebounds the spell rather than shrugging it off: the ward
    // does not save the stack from the effect, it hands the effect to somebody
    // on the caster's own side. Rolled after resistance, because a stack that
    // shrugged the spell off never had one to reflect.
    const bounced = mirrored(c, content, slot, side);
    if (bounced >= 0) {
      if (!affected.includes(bounced)) affected.push(bounced);
      c.events.push({ type: "BattleSpellReflected", from: slot, to: bounced, spell: def.id });
      continue;
    }
    affected.push(slot);
  }

  const eventHex = targets.hex >= 0
    ? targets.hex
    : affected.length > 0 ? c.s.stacks.hex[affected[0]!]! : -1;
  c.events.push({
    type: "BattleSpellCast", side, spell: def.id, target: eventHex, affected: [...affected],
  });

  // Recasting refreshes rather than stacks: the previous copy of this spell's
  // own modifiers goes before any of the new ones land, so a spell whose expert
  // bonus is a second MODIFIER effect does not wipe its own first one.
  for (const slot of affected) removeModsBySource(c, slot, def.id as string);

  // The expert bonus runs first. It raises the spell's own numbers, and a rule
  // effect that reads a stat at cast time - Bless flooring damage at the top of
  // the range - has to see the raised one, not the stat it is about to become.
  const effects: SpellEffectSpec[] = [];
  if (prof.mastery >= 3 && def.expertBonus !== undefined) effects.push(def.expertBonus);
  for (const e of def.effects) effects.push(e);

  for (const e of effects) {
    if (affected.length === 0) {
      applyEffect(c, content, def, e, prof, casterStack, -1, targets.hex, 0);
      continue;
    }
    for (const slot of affected) {
      // How many jumps a chaining bolt took to reach this stack. It is read off
      // the target list rather than off `affected`, so a stack that resisted or
      // rebounded does not promote the ones behind it to full damage; a stack
      // the mirror handed the spell to is not on the chain at all and takes it
      // whole, which is what "the ward gave your spell to somebody else" means.
      const jump = Math.max(0, targets.stacks.indexOf(slot));
      applyEffect(c, content, def, e, prof, casterStack, slot, targets.hex, jump);
    }
  }
}

// ------------------------------------------------------------- hero casting

export interface PreparedCast {
  readonly def: SpellDef;
  readonly prof: MagicProfile;
  readonly targets: CastTargets;
  readonly cost: number;
}

/**
 * Every reason a hero's cast can be refused, in the order the classic asks them: the
 * book, the spell, the round, the school, the mana, then the target. Pure - it
 * rolls nothing - so `enumerateHeroCasts` can call it per candidate and the
 * legality contract holds by construction.
 */
interface SpellPrep {
  readonly def: SpellDef;
  readonly prof: MagicProfile;
  readonly cost: number;
}

/** Everything about a cast that does not depend on the target. */
function prepareHeroSpell(
  s: BattleState, content: ContentRegistry, side: 0 | 1, spell: SpellDefId,
  mods: readonly Modifier[],
): Result<SpellPrep, Reason> {
  const hero = s.heroes[side];
  if (hero.id === null || hero.spellbook.length === 0) return Err({ code: "NO_SPELLBOOK" });
  if (!hero.spellbook.includes(spell)) return Err({ code: "SPELL_NOT_KNOWN", spell });
  const def = content.spells.get(spell);
  if (def === undefined) return Err({ code: "NO_SUCH_ENTITY", id: spell as string });
  if (!def.combat) return Err({ code: "TARGET_INVALID" });
  if (hero.castThisRound) return Err({ code: "ALREADY_CAST_THIS_ROUND" });

  // A hero casts during one of its own stacks' turns, and that stack loses its
  // action for it (Section 52, "one hero spell per round").
  const active = activeStack(s);
  if (active < 0 || s.stacks.side[active] !== side) {
    return Err({ code: "STACK_NOT_ACTIVE", stack: active });
  }

  const prof = magicProfileFrom(s, side, def, mods);
  // Until the adventure kernel carries the profile there is no Wisdom in state
  // to gate on, and the spellbook is the only gate the hero has passed.
  if (prof.carried && def.level > (WISDOM_SPELL_LEVEL[prof.wisdom] ?? 1)) {
    return Err({ code: "TARGET_INVALID" });
  }
  // The field's own gates: a Oathbreaker's Cloak caps what may be cast and a Cape
  // of Silence floors it, and both hold for whichever hero is asking.
  const cap = resolveStat(0, modsWithStat(mods, STAT_SPELL_LEVEL_CAP)).value;
  if (cap > 0 && def.level > cap) return Err({ code: "TARGET_INVALID" });
  const floor = resolveStat(0, modsWithStat(mods, STAT_SPELL_LEVEL_FLOOR)).value;
  if (floor > 0 && def.level <= floor) return Err({ code: "TARGET_INVALID" });
  // The Dusk Seal, read across the field rather than from `mods`: it is
  // the OTHER hero's kit that closes a school against this one. A spell of
  // school "all" belongs to none of them and is never sealed.
  if (def.school !== "all") {
    const foe = sideMagicMods(s, (1 - side) as 0 | 1);
    const sealed = resolveStat(0, modsWithStat(foe, sealedSchoolStat(def.school))).value;
    if (sealed > 0) return Err({ code: "TARGET_INVALID" });
  }
  const bad = unsupportedEffect(def, prof.mastery, s.siege !== null && s.siege.active);
  if (bad !== null) return Err({ code: "NOT_IMPLEMENTED", what: `spell effect ${bad}` });

  const cost = spellManaCost(def, prof);
  if (hero.mana < cost) return Err({ code: "OUT_OF_MANA", have: hero.mana, need: cost });
  return Ok({ def, prof, cost });
}

export function validateHeroCast(
  s: BattleState, content: ContentRegistry, side: 0 | 1, spell: SpellDefId, target: number,
): Result<PreparedCast, Reason> {
  const prep = prepareHeroSpell(s, content, side, spell, sideMagicMods(s, side));
  if (!prep.ok) return prep;
  const targets = resolveTargets(s, content, prep.value.def, side, target, prep.value.prof.mastery);
  if (!targets.ok) return targets;
  return Ok({ ...prep.value, targets: targets.value });
}

/** Execute a validated hero cast: mana, the once-per-round latch, the effects. */
export function castHeroSpell(
  c: Ctx, content: ContentRegistry, side: 0 | 1, spell: SpellDefId, target: number,
): Result<number, Reason> {
  const r = validateHeroCast(c.s, content, side, spell, target);
  if (!r.ok) return r;
  // Read the stack whose turn is being spent BEFORE the spell lands: a hero can
  // kill its own active stack (Worldfire, a Fireball on its own line), and if
  // the slot were read afterwards the turn machine would never be told to move
  // on and the battle would stall on a corpse.
  const caster = activeStack(c.s);
  const hero = c.s.heroes[side];
  jSetField(c.j, hero as unknown as object, "mana", hero.mana - r.value.cost);
  jSetField(c.j, hero as unknown as object, "castThisRound", true);
  resolveCast(c, content, r.value.def, r.value.prof, side, -1, r.value.targets);
  return Ok(caster);
}

// --------------------------------------------------------- creature casting

export function validateCreatureCast(
  s: BattleState, content: ContentRegistry, slot: number, spell: SpellDefId, target: number,
): Result<PreparedCast, Reason> {
  if (!isAlive(s.stacks, slot)) return Err({ code: "STACK_DEAD", stack: slot });
  let power = -1;
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind !== "CASTER" || !a.spells.includes(spell)) continue;
    power = a.power;
  }
  if (power < 0) return Err({ code: "SPELL_NOT_KNOWN", spell });
  if (s.stacks.casts[slot]! <= 0) return Err({ code: "NO_SHOTS_LEFT" });
  // Cooling. The same `StackStore.cooldown` an ACTIVATED ability uses and the
  // same countdown ticks it, so a stack that carries both spends one timer
  // between them - "this stack's special is used up", not one clock per
  // ability. `NO_SHOTS_LEFT` is the reason ACTIVATED gives for the same state.
  if (s.stacks.cooldown[slot]! > 0) return Err({ code: "NO_SHOTS_LEFT" });
  const def = content.spells.get(spell);
  if (def === undefined) return Err({ code: "NO_SUCH_ENTITY", id: spell as string });
  if (!def.combat) return Err({ code: "TARGET_INVALID" });

  const prof = creatureProfile(power);
  const bad = unsupportedEffect(def, prof.mastery, s.siege !== null && s.siege.active);
  if (bad !== null) return Err({ code: "NOT_IMPLEMENTED", what: `spell effect ${bad}` });

  const side = s.stacks.side[slot] === 1 ? 1 : 0;
  const targets = resolveTargets(s, content, def, side, target, prof.mastery);
  if (!targets.ok) return targets;
  return Ok({ def, prof, targets: targets.value, cost: 0 });
}

/**
 * Cast after shot: a Brine Hexer lays a spell on whatever it just shot.
 *
 * Free in every sense the expansion means - it costs no cast, no action and no mana,
 * and it happens as part of the shot rather than instead of one. The list is
 * walked in order and the FIRST spell the target is not already under is the
 * one that lands, which is how "Weakness, or Sundering Ray if Weakness is
 * already active" is expressed without either spell being named in code.
 *
 * Lives here rather than in `abilities.ts` because casting is this file's, and
 * `spells.ts` already imports `abilities.ts` - the other direction would close
 * a cycle. `execute.ts` imports both and calls this from `strike`.
 */
export function castAfterShot(
  c: Ctx, content: ContentRegistry, attacker: number, target: number,
): void {
  const st = c.s.stacks;
  if (!isAlive(st, attacker) || !isAlive(st, target)) return;
  if (st.side[attacker] === st.side[target]) return;
  for (const a of abilitiesOf(content, st.def[attacker]!)) {
    if (a.kind !== "CAST_AFTER_SHOT") continue;
    const side = st.side[attacker] === 1 ? 1 : 0;
    const prof = creatureProfile(a.power);
    for (const id of a.spells) {
      const def = content.spells.get(id);
      if (def === undefined) continue;
      if (spellImmune(c.s, content, target, def)) continue;
      // Already under it: try the next in the list, which is the whole point
      // of the list being ordered. "Under it" is asked of the spell's own
      // modifier sources rather than of a name built from its id: the shipped
      // ids already carry the `spell.` prefix and the fixtures do not, and a
      // guess at the convention matched nothing, so the list never advanced.
      const sources = new Set<string>();
      for (const e of def.effects) {
        if (e.kind === "MODIFIER") for (const m of e.modifiers ?? []) sources.add(m.source as string);
      }
      let already = false;
      for (const m of st.mods[target] ?? []) {
        if (sources.has(m.source as string)) { already = true; break; }
      }
      if (already) continue;
      resolveCast(c, content, def, prof, side, target, { stacks: [target], hex: st.hex[target]! });
      return;
    }
  }
}

export function castCreatureSpell(
  c: Ctx, content: ContentRegistry, slot: number, spell: SpellDefId, target: number,
): Result<true, Reason> {
  const r = validateCreatureCast(c.s, content, slot, spell, target);
  if (!r.ok) return r;
  jSetI32(c.j, c.s.stacks.casts, slot, c.s.stacks.casts[slot]! - 1);
  // Set BEFORE the effect, unlike `activate`: a spell can kill its target and
  // `resolveCast` can push events that read the board, and the cooldown is a
  // fact about the caster either way. Authored-absent means no cooldown, which
  // is what every caster shipped before this could do.
  for (const a of abilitiesOf(content, c.s.stacks.def[slot]!)) {
    if (a.kind !== "CASTER" || !a.spells.includes(spell)) continue;
    const cool = a.cooldownRounds ?? 0;
    if (cool > 0) jSetI32(c.j, c.s.stacks.cooldown, slot, cool);
  }
  const side = c.s.stacks.side[slot] === 1 ? 1 : 0;
  resolveCast(c, content, r.value.def, r.value.prof, side, slot, r.value.targets);
  return Ok(true);
}

// ------------------------------------------------------------------ legality

/** Hexes worth centring an AREA spell on: any hex a live stack stands on or touches. */
function candidateHexes(s: BattleState): readonly number[] {
  const seen = new Int32Array(BF_HEXES);
  const out: number[] = [];
  for (let i = 0; i < s.stacks.n; i++) {
    if (!isAlive(s.stacks, i)) continue;
    const k = stackHexes(s.stacks, i, HEXES);
    for (let m = 0; m < k; m++) {
      const h = HEXES[m]!;
      const area: number[] = new Array(7).fill(-1);
      const n = areaHexes(h, area);
      for (let a = 0; a < n; a++) {
        const g = area[a]!;
        if (g < 0 || seen[g] === 1) continue;
        seen[g] = 1;
        out.push(g);
      }
    }
  }
  out.sort((a, b) => a - b);
  return out;
}

function targetCandidates(
  s: BattleState, def: SpellDef, hexes: readonly number[], mastery: number,
): readonly number[] {
  switch (effectiveTargeting(def, mastery)) {
    case "SINGLE_ALLY": case "SINGLE_ENEMY": case "SINGLE_ANY": {
      const out: number[] = [];
      for (let i = 0; i < s.stacks.n; i++) if (isAlive(s.stacks, i)) out.push(i);
      return out;
    }
    case "AREA": case "HEX": return hexes;
    case "SELF": case "NONE": case "ALL": case "ALL_ALLIES": case "ALL_ENEMIES": return [-1];
    case "MAP": return [];
  }
}

/**
 * Every cast the hero on `side` may legally make right now. Each candidate is
 * put through `validateHeroCast`, so §3.3's contract - everything enumerated
 * executes `Ok` - is true by construction rather than by agreement.
 */
export function enumerateHeroCasts(
  s: BattleState, side: 0 | 1, content: ContentRegistry,
): readonly BattleCommand[] {
  const out: BattleCommand[] = [];
  const hero = s.heroes[side];
  if (hero.id === null || hero.spellbook.length === 0 || hero.castThisRound) return out;
  const active = activeStack(s);
  if (active < 0 || s.stacks.side[active] !== side) return out;
  // The side's magic profile and the area-centre candidates do not vary from
  // spell to spell, and the profile does not vary from target to target, so
  // both are computed once: this is enumerated on every AI ply.
  const mods = sideMagicMods(s, side);
  let hexes: readonly number[] | null = null;
  for (const spell of hero.spellbook) {
    const prep = prepareHeroSpell(s, content, side, spell, mods);
    if (!prep.ok) continue;
    const def = prep.value.def;
    const mastery = prep.value.prof.mastery;
    if (def.targeting === "AREA" || def.targeting === "HEX") hexes ??= candidateHexes(s);
    for (const target of targetCandidates(s, def, hexes ?? [], mastery)) {
      if (resolveTargets(s, content, def, side, target, mastery).ok) {
        out.push({ type: "BATTLE_CAST", side, spell, target });
      }
    }
  }
  return out;
}

export function enumerateCreatureCasts(
  s: BattleState, slot: number, content: ContentRegistry,
): readonly BattleCommand[] {
  const out: BattleCommand[] = [];
  if (slot < 0 || !isAlive(s.stacks, slot) || s.stacks.casts[slot]! <= 0) return out;
  if ((s.stacks.flags[slot]! & F_ACTED) !== 0) return out;
  // Offering a cast the executor refuses is how the AI ends up with a scored
  // command it cannot play; `validateCreatureCast` checks this too, and this
  // is the cheap check that keeps it out of the list at all.
  if (s.stacks.cooldown[slot]! > 0) return out;
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind !== "CASTER") continue;
    for (const id of a.spells) {
      const def = content.spells.get(id);
      if (def === undefined || !def.combat) continue;
      const hexes = def.targeting === "AREA" || def.targeting === "HEX" ? candidateHexes(s) : [];
      for (const target of targetCandidates(s, def, hexes, 0)) {
        if (validateCreatureCast(s, content, slot, id, target).ok) {
          out.push({ type: "BATTLE_CREATURE_CAST", stack: slot, spell: id, target });
        }
      }
    }
  }
  return out;
}
